import Vue from 'vue';
import Vuex from 'vuex';
import OfflineStorage from '../offlineStorage';
import router from '../router';
import auth from '../auth';
import gbaApi from '../api/gbaApi';
import qapApi from '../api/qapApi';
import graphApi from '../api/graphApi';

Vue.use(Vuex)
Vue.config.devtools = true

export default new Vuex.Store({
  state: {
    isAuthenticated: false,
    isOnline: navigator.onLine,
    settings: {},
    userProfile: null
  },
  mutations: {
    REPLACE_STATE_PROP: (state, payload) => {
      state[payload.property] = payload.data;
    }
  },
  actions: {
    async initializeStore(context) {
      // Get Settings
      let settings = await OfflineStorage.getItem("settings");
      if (settings == null) {
        settings = {
          isDarkMode: false,
          farmLineMg: []
        };
      }
      context.commit("REPLACE_STATE_PROP", { property: 'settings', data: settings });

      // Get User Profile
      let userProfile = await OfflineStorage.getItem('userProfile');
      if (!context.state.isOnline && userProfile != null) {
        context.commit("REPLACE_STATE_PROP", { property: "userProfile", data: userProfile });

        if (router.currentRoute && router.currentRoute.path !== '/') {
          router.push({ path: '/' });
        }
        return;
      }

      const response = await auth.ssoSilent();
      if (response) {
        context.commit("REPLACE_STATE_PROP", { property: "isAuthenticated", data: true });
        await setUserProfile(context);
      }
    },
    async storeSignIn(context) {
      await auth.signIn();

      context.commit("REPLACE_STATE_PROP", { property: "isAuthenticated", data: true });
      
      setUserProfile(context);

      if (router.currentRoute.path !== '/') {
        router.push({ path: '/' });
      }
    },
    async storeSignOut(context) {
      auth.signOut();

      context.commit("REPLACE_STATE_PROP", { property: "userProfile", data: null });
      context.commit("REPLACE_STATE_PROP", { property: "isAuthenticated", data: false });

      await OfflineStorage.setItem('userProfile', null);

      if (router.currentRoute.path !== '/signin') {
        router.push({ path: '/signin' });
      }
    },
    async setOnlineStatusInStore(context, isOnline) { 
      context.commit('REPLACE_STATE_PROP', { property: 'isOnline', data: isOnline });
      if (isOnline && 
        !context.state.isAuthenticated) {
          await auth.ssoSilent(context);
      }
    },
    updateSettingsInStore(context, settings) { 
      context.commit('REPLACE_STATE_PROP', { property: 'settings', data: settings });
      OfflineStorage.setItem('settings', settings);
    },
    async loadFarmSettingsFromServer(context, {settings, root}) {
      const farmLineMg = await gbaApi.getFarmLineMg();
      settings.farmLineMg = farmLineMg;
      console.log("FarmLineMg Info:", toRaw(farmLineMg));
      
      const farmBatchInfo = await qapApi.getBatchInfo(settings.currentFarm);
      settings.farmBatchInfo = farmBatchInfo;
      console.log("QAP Batch Info:", toRaw(farmBatchInfo));
  
      context.commit("REPLACE_STATE_PROP", { property: 'settings', data: settings });
      await OfflineStorage.setItem("settings", settings);

      try {
        await syncArchiveFromServer(settings, root);
      }
      catch(error) {
        root.showNoticeError(`Unable to retrieve Genomic Bleed groups from server: ${error.message}`);
      }
    }
  }
});

async function setUserProfile(context) {
  const userProfile = await graphApi.getUserProfile();
  if (userProfile) {
    context.commit("REPLACE_STATE_PROP", { property: "userProfile", data: userProfile });
    await OfflineStorage.setItem('userProfile', userProfile);
  } else {
    context.commit("REPLACE_STATE_PROP", { property: "userProfile", data: null });
    await OfflineStorage.setItem('userProfile', null);
  }
}

async function syncArchiveFromServer(settings, root) {
  var serverGroups = await gbaApi.getGenomicBleedList(settings.currentFarm);
  var archivedGroups = await OfflineStorage.getItem('archivedGenomicBleedGroups');
  if (archivedGroups == null) archivedGroups = [];
  var newSummaryGroups = getNewGenomicBleedGroupSummary(serverGroups, archivedGroups);
  for (var i=0; i< newSummaryGroups.length; i++) {
    await addGenomicBleedGroupToArchive(newSummaryGroups[i], archivedGroups, root);
  }
}

function toRaw(obj) {
  return JSON.parse(JSON.stringify(obj));
}

async function addGenomicBleedGroupToArchive(ns, archivedGroups, root) {
  // Get samples for Group from server
  var newGroupSamples = await gbaApi.getGenomicBleed(ns);
  let trex = '';
  let gender = null;
  let operator = null;
  let notes = null;

  if (newGroupSamples.length > 0) {
    trex = newGroupSamples[0].trex;
    gender = newGroupSamples[0].gender;
    operator = newGroupSamples[0].operator;
    notes = newGroupSamples[0].notes;
  }

  // build group Name.
  let groupName = `${ns.farm}_${ns.line}_MG${ns.minigeneration}`;
  if (ns.hatch != "0") groupName += `_H${ns.hatch}`;
  groupName += `_${ns.disp}`; 
  if (trex.length > 0) {
    groupName = `${trex}_${groupName}`;
  }

  // Add/Replace new Archive Group
  const newArchiveGroup = {
    groupName: groupName,
    checkWingband: false,
    deleteDate: null,
    disp: ns.disp,
    farm: ns.farm,
    gender: gender,
    hatch: ns.hatch,
    line: ns.line,
    minigeneration: ns.minigeneration,
    notes: notes,
    operator: operator,
    restoreDate: null, 
    samples: newGroupSamples,
    trex: trex,
    uploadDate: ns.uploadGmtDateTime
  };

  const index = archivedGroups.findIndex(x => x.groupName === groupName && x.uploadDate === ns.uploadDate);
  if (index >= 0) {
    archivedGroups[index] = newArchiveGroup;
  }
  else {
    archivedGroups.push(newArchiveGroup);
  }
  await OfflineStorage.setItem('archivedGenomicBleedGroups', archivedGroups);

  root.showNoticeSuccess(`Loaded Genomic Bleed Group (${newArchiveGroup.groupName}) from server.`);
}

function getNewGenomicBleedGroupSummary(serverGroups, archivedGroups) {
  var uploadedArchivedGroups = archivedGroups.filter(ag => ag.uploadDate != null);
  var missingGroups = [];
  serverGroups.forEach (bli => {
    var grp = uploadedArchivedGroups.find(x => {
      if (x.farm == bli.farm
        && x.disp == bli.disp 
        && x.line == bli.line 
        && x.minigeneration == bli.minigeneration
        && x.hatch == bli.hatch
        && x.uploadDate == bli.uploadGmtDateTime)
      {
        return true;
      }
      else {
        return false;
      }
    });
    if (grp == null) {
      missingGroups.push(bli);
    }
  });

  return missingGroups;
}